// @core/stores/tenant/TenantStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { tenantApi } from '@core/api';
import type {
  AddTenantMemberRequest,
  ApproveTenantMigrationRequest,
  CancelTenantMigrationRequest,
  CompleteTenantOnboardingRequest,
  ExportTenantDataRequest,
  GetTenantSuspensionHistoryResponse,
  ListTenantDataExportsResponse,
  ListTenantMembersResponse,
  ListTenantMigrationsResponse,
  ReactivateTenantRequest,
  RequestTenantMigrationRequest,
  SuspendTenantRequest,
  Tenant,
  TenantAnalyticsResponse,
  TenantDataExportResponse,
  TenantEntitlementsResponse,
  TenantMemberResponse,
  TenantMigrationResponse,
  TenantOnboardingResponse,
  TenantQuotaResponse,
  TenantResponse,
  TenantSSOConfigResponse,
  TenantSettingsResponse,
  TestTenantSSOConfigRequest,
  TestTenantSSOConfigResponse,
  TransferTenantOwnershipRequest,
  UpdateTenantMemberRoleRequest,
  UpdateTenantOnboardingRequest,
  UpdateTenantQuotaRequest,
  UpdateTenantSSOConfigRequest,
  UpdateTenantSettingsRequest,
} from '@core/types/tenant';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TenantStore {
  // List state
  items: Tenant[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Tenant | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Tenant>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Tenant | undefined {
    return this.cache.get(id);
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add member to tenant
   */
  async AddTenantMember(tenant_id: string, data: AddTenantMemberRequest): Promise<TenantMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.AddTenantMember(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Approve tenant migration
   */
  async ApproveTenantMigration(tenant_id: string, migration_id: string, data: ApproveTenantMigrationRequest): Promise<TenantMigrationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ApproveTenantMigration(tenant_id, migration_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel tenant migration
   */
  async CancelTenantMigration(tenant_id: string, migration_id: string, data: CancelTenantMigrationRequest): Promise<TenantMigrationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.CancelTenantMigration(tenant_id, migration_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Mark tenant onboarding as complete
   */
  async CompleteTenantOnboarding(tenant_id: string, data: CompleteTenantOnboardingRequest): Promise<TenantOnboardingResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.CompleteTenantOnboarding(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete tenant SSO configuration
   */
  async DeleteTenantSSOConfig(tenant_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await tenantApi.DeleteTenantSSOConfig(tenant_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== tenant_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === tenant_id) {
          this.currentItem = null;
        }

        this.removeFromCache(tenant_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Request tenant data export
   */
  async ExportTenantData(tenant_id: string, data: ExportTenantDataRequest): Promise<TenantDataExportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ExportTenantData(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant analytics summary
   */
  async GetTenantAnalytics(tenant_id: string): Promise<TenantAnalyticsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantAnalytics(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get data export status
   */
  async GetTenantDataExport(tenant_id: string, export_id: string): Promise<TenantDataExportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantDataExport(tenant_id, export_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant entitlements
   */
  async GetTenantEntitlements(tenant_id: string): Promise<TenantEntitlementsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantEntitlements(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get migration status
   */
  async GetTenantMigration(tenant_id: string, migration_id: string): Promise<TenantMigrationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantMigration(tenant_id, migration_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant onboarding progress
   */
  async GetTenantOnboarding(tenant_id: string): Promise<TenantOnboardingResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantOnboarding(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant quota
   */
  async GetTenantQuota(tenant_id: string): Promise<TenantQuotaResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantQuota(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant SSO configuration
   */
  async GetTenantSSOConfig(tenant_id: string): Promise<TenantSSOConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantSSOConfig(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant settings
   */
  async GetTenantSettings(tenant_id: string): Promise<TenantSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantSettings(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get tenant suspension history
   */
  async GetTenantSuspensionHistory(tenant_id: string): Promise<GetTenantSuspensionHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.GetTenantSuspensionHistory(tenant_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tenant data exports
   */
  async ListTenantDataExports(tenant_id: string, params?: PaginatedRequest): Promise<ListTenantDataExportsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ListTenantDataExports(tenant_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tenant members
   */
  async ListTenantMembers(tenant_id: string, params?: PaginatedRequest): Promise<ListTenantMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ListTenantMembers(tenant_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tenant migrations
   */
  async ListTenantMigrations(params?: PaginatedRequest & { status?: string | number | boolean; }): Promise<ListTenantMigrationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ListTenantMigrations(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reactivate suspended tenant
   */
  async ReactivateTenant(tenant_id: string, data: ReactivateTenantRequest): Promise<TenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.ReactivateTenant(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove member from tenant
   */
  async RemoveTenantMember(tenant_id: string, user_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.RemoveTenantMember(tenant_id, user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request tenant migration
   */
  async RequestTenantMigration(tenant_id: string, data: RequestTenantMigrationRequest): Promise<TenantMigrationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.RequestTenantMigration(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Suspend tenant
   */
  async SuspendTenant(tenant_id: string, data: SuspendTenantRequest): Promise<TenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.SuspendTenant(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test SSO configuration
   */
  async TestTenantSSOConfig(tenant_id: string, data: TestTenantSSOConfigRequest): Promise<TestTenantSSOConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.TestTenantSSOConfig(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Transfer tenant ownership
   */
  async TransferTenantOwnership(tenant_id: string, data: TransferTenantOwnershipRequest): Promise<TenantResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.TransferTenantOwnership(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update member role in tenant
   */
  async UpdateTenantMemberRole(tenant_id: string, user_id: string, data: UpdateTenantMemberRoleRequest): Promise<TenantMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.UpdateTenantMemberRole(tenant_id, user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant onboarding progress
   */
  async UpdateTenantOnboarding(tenant_id: string, data: UpdateTenantOnboardingRequest): Promise<TenantOnboardingResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.UpdateTenantOnboarding(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant quota
   */
  async UpdateTenantQuota(tenant_id: string, data: UpdateTenantQuotaRequest): Promise<TenantQuotaResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.UpdateTenantQuota(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant SSO configuration
   */
  async UpdateTenantSSOConfig(tenant_id: string, data: UpdateTenantSSOConfigRequest): Promise<TenantSSOConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.UpdateTenantSSOConfig(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tenant settings (including privacy)
   */
  async UpdateTenantSettings(tenant_id: string, data: UpdateTenantSettingsRequest): Promise<TenantSettingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tenantApi.UpdateTenantSettings(tenant_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const tenantStore = new TenantStore();
